/**
 * InstancedTrack.tsx
 *
 * High-performance GPU Instanced Dual-Rail Track System.
 * Follows official Three.js instancing architecture:
 * Renders BOTH straight dual-rails and curved dual-rail chord segments using
 * shared GPU InstancedMeshes (Top Black, Upper White, Middle Black, Lower White).
 *
 * Reduces thousands of track draw calls across large Fab layouts down to
 * exactly 2-4 draw calls with 100% visual fidelity preservation.
 */

import React, { useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import type { LayoutElement } from '../../types';
import {
  ALL_RAIL_TYPES,
  isCurveRailType,
  isJunctionRailType,
  isConnectorRailType,
  resolveRailType,
  getCurveRadius,
  getCurveSweepAngle,
  getCurveSweepSign,
  getStraightRailCircleJunctionReach,
  resolveStraightRail3DSpan,
} from '../../utils/topology';
import {
  TRACK_3D_MIN_THICKNESS,
  TRACK_3D_THICKNESS,
  getElement3DGroupY,
  getElement3DExtrusionDepth,
} from '../../utils/layout3d';
import { metersToEffectiveWorldUnits } from '../../utils/measurement';
import {
  DUAL_RAIL_LIFT,
  getDualRailProfile,
} from '../../components/canvas/DualRailTrack3D';
import { getRailBarMaterial, RAIL_POOL } from '../../utils/threeResourcePool';
import { BLENDER_TRACK } from '../../components/canvas/DualRailTrack3D';
import { useRailElevation } from '../../components/canvas/railElevationContext';
import { instanceRegistry } from './InstanceRegistry';

const UNIT_BOX = new THREE.BoxGeometry(1, 1, 1);
const TEMP_OBJECT = new THREE.Object3D();

export type InstancedTrackProps = {
  elements: Record<string, LayoutElement>;
  elementIds: readonly string[];
  floorPlanScaleFactor?: number;
  dxfUnitsPerMeter?: number;
  hasFloorCal?: boolean;
  onSelect?: (entityId: string) => void;
};

export type TrackBarInstance = {
  matrix: THREE.Matrix4;
  entityId: string;
};

export type BatchedTrackData = {
  webBars: TrackBarInstance[];
  railBars: TrackBarInstance[];
  batchedIds: Set<string>;
};

export function getTrackStrokeWidth(
  el: LayoutElement,
  floorPlanScaleFactor?: number,
  dxfUnitsPerMeter?: number
): number {
  const thicknessMeters = Number(el.customProperties?.thicknessMeters);
  if (Number.isFinite(thicknessMeters) && thicknessMeters > 0) {
    return Math.max(
      TRACK_3D_MIN_THICKNESS,
      metersToEffectiveWorldUnits(thicknessMeters, floorPlanScaleFactor, dxfUnitsPerMeter)
    );
  }
  return TRACK_3D_THICKNESS;
}

export function buildInstancedTrackData(
  elements: Record<string, LayoutElement>,
  elementIds: readonly string[],
  floorPlanScaleFactor?: number,
  dxfUnitsPerMeter?: number,
  hasFloorCal?: boolean,
  railElevation?: number
): BatchedTrackData {
  const webBars: TrackBarInstance[] = [];
  const railBars: TrackBarInstance[] = [];
  const batchedIds = new Set<string>();

  const scaleFactor = hasFloorCal ? floorPlanScaleFactor : undefined;
  const dxfUnits = hasFloorCal ? dxfUnitsPerMeter : undefined;
  const layoutRails = Object.values(elements);

  for (const id of elementIds) {
    const el = elements[id];
    if (!el || !ALL_RAIL_TYPES.has(el.type)) continue;

    const railType = resolveRailType(el);
    if (isJunctionRailType(railType) || isConnectorRailType(railType)) continue;

    const w = el.dimensions.width;
    const h = el.dimensions.height;
    const depth = getElement3DExtrusionDepth(el);
    const strokeW = getTrackStrokeWidth(el, scaleFactor, dxfUnits);
    const { railH, glowW } = getDualRailProfile(strokeW, depth);
    const trackW = Math.max(glowW, strokeW * 0.24);

    const l1H = railH * 0.12;
    const layerBelowH = (railH - l1H) / 3;
    const l2H = layerBelowH;
    const l3H = layerBelowH;
    const l4H = layerBelowH;

    const topRelY = railH * 0.5;
    const l1RelY = topRelY - l1H * 0.5;
    const l2RelY = topRelY - l1H - layerBelowH * 0.5;
    const l3RelY = topRelY - l1H - layerBelowH * 1.5;
    const l4RelY = topRelY - l1H - layerBelowH * 2.5;

    const baseRotY = (-(el.rotation || 0) * Math.PI) / 180;
    const groupY = getElement3DGroupY(el, undefined, railElevation);

    if (isCurveRailType(railType)) {
      // Decompose curve into chords matching DualRailCurve3D
      const radius = getCurveRadius(w, h);
      const sweepRad =
        (getCurveSweepSign(el) * getCurveSweepAngle(railType) * Math.PI) / 180;
      const steps = 12;
      const junctionReach = getStraightRailCircleJunctionReach(el, layoutRails, strokeW);
      const tipPad = Math.max(strokeW * 0.75, 2);
      const seamOverlap = Math.max(strokeW * 0.4, 1);

      const cos = Math.cos(baseRotY);
      const sin = Math.sin(baseRotY);

      for (let i = 0; i < steps; i++) {
        const a0 = (i / steps) * sweepRad;
        const a1 = ((i + 1) / steps) * sweepRad;
        const x0 = Math.cos(a0) * radius;
        const z0 = Math.sin(a0) * radius;
        const x1 = Math.cos(a1) * radius;
        const z1 = Math.sin(a1) * radius;
        const chordLen = Math.hypot(x1 - x0, z1 - z0);
        let len = chordLen + seamOverlap;
        if (i === 0) len += Math.max(tipPad, junctionReach.extendStart);
        if (i === steps - 1) len += Math.max(tipPad, junctionReach.extendEnd);

        const mx = (x0 + x1) * 0.5;
        const mz = (z0 + z1) * 0.5;
        const chordRotY = -Math.atan2(z1 - z0, x1 - x0);
        const totalRotY = baseRotY + chordRotY;

        // Position in world space: el.position + rotY(mx, mz)
        const wx = el.position.x + mx * cos + mz * sin;
        const wy = groupY + DUAL_RAIL_LIFT;
        const wz = el.position.y - mx * sin + mz * cos;

        // Push 4 layers for this chord
        const pushChordBar = (isWeb: boolean, relY: number, barH: number) => {
          TEMP_OBJECT.position.set(wx, wy + relY, wz);
          TEMP_OBJECT.rotation.set(0, totalRotY, 0);
          TEMP_OBJECT.scale.set(len, barH, trackW);
          TEMP_OBJECT.updateMatrix();
          const target = isWeb ? webBars : railBars;
          target.push({
            matrix: TEMP_OBJECT.matrix.clone(),
            entityId: id,
          });
        };

        pushChordBar(true, l1RelY, l1H); // Layer 1 (Top Black)
        pushChordBar(false, l2RelY, l2H); // Layer 2 (Upper White)
        pushChordBar(true, l3RelY, l3H); // Layer 3 (Middle Black)
        pushChordBar(false, l4RelY, l4H); // Layer 4 (Lower White)
      }

      batchedIds.add(id);
      continue;
    }

    const { span, centerLocalX, centerLocalZ, alongX } = resolveStraightRail3DSpan(
      el,
      layoutRails,
      strokeW
    );
    const localRotY = alongX ? 0 : Math.PI / 2;
    const totalRotY = baseRotY + localRotY;

    const cos = Math.cos(baseRotY);
    const sin = Math.sin(baseRotY);

    const pushStraightBar = (isWeb: boolean, relY: number, barH: number) => {
      const bx = centerLocalX;
      const bz = centerLocalZ;
      const wx = el.position.x + bx * cos + bz * sin;
      const wy = groupY + DUAL_RAIL_LIFT + relY;
      const wz = el.position.y - bx * sin + bz * cos;

      TEMP_OBJECT.position.set(wx, wy, wz);
      TEMP_OBJECT.rotation.set(0, totalRotY, 0);
      TEMP_OBJECT.scale.set(span, barH, trackW);
      TEMP_OBJECT.updateMatrix();

      const target = isWeb ? webBars : railBars;
      target.push({
        matrix: TEMP_OBJECT.matrix.clone(),
        entityId: id,
      });
    };

    pushStraightBar(true, l1RelY, l1H);
    pushStraightBar(false, l2RelY, l2H);
    pushStraightBar(true, l3RelY, l3H);
    pushStraightBar(false, l4RelY, l4H);

    batchedIds.add(id);
  }

  return { webBars, railBars, batchedIds };
}

export const InstancedTrackMeshLayer: React.FC<{
  layerId: string;
  bars: readonly TrackBarInstance[];
  material: THREE.Material;
  onSelect?: (entityId: string) => void;
}> = React.memo(({ layerId, bars, material, onSelect }) => {
  const meshRef = useRef<THREE.InstancedMesh>(null);

  useLayoutEffect(() => {
    instanceRegistry.clear(layerId);
    for (let i = 0; i < bars.length; i++) {
      instanceRegistry.register(layerId, i, bars[i]!.entityId);
    }
  }, [layerId, bars]);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh || bars.length === 0) return;

    mesh.count = bars.length;
    for (let i = 0; i < bars.length; i++) {
      mesh.setMatrixAt(i, bars[i]!.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();
    mesh.computeBoundingBox();
  }, [bars]);

  if (bars.length === 0) return null;

  const handleClick = onSelect
    ? (e: any) => {
        instanceRegistry.handleClick(layerId, e, onSelect);
      }
    : undefined;

  return (
    <instancedMesh
      ref={meshRef}
      args={[UNIT_BOX, material, bars.length]}
      material={material}
      castShadow
      receiveShadow
      onClick={handleClick}
      frustumCulled
      dispose={null}
    />
  );
});

InstancedTrackMeshLayer.displayName = 'InstancedTrackMeshLayer';

export const InstancedTrack: React.FC<InstancedTrackProps> = React.memo(
  ({
    elements,
    elementIds,
    floorPlanScaleFactor,
    dxfUnitsPerMeter,
    hasFloorCal,
    onSelect,
  }) => {
    const railElevation = useRailElevation();
    const { webBars, railBars } = useMemo(
      () =>
        buildInstancedTrackData(
          elements,
          elementIds,
          floorPlanScaleFactor,
          dxfUnitsPerMeter,
          hasFloorCal,
          railElevation
        ),
      [elements, elementIds, floorPlanScaleFactor, dxfUnitsPerMeter, hasFloorCal, railElevation]
    );

    const materialRail = useMemo(
      () =>
        getRailBarMaterial(BLENDER_TRACK.railWhite, {
          ...RAIL_POOL.aluminum,
          opacity: 1,
        }),
      []
    );
    const materialWeb = useMemo(
      () =>
        getRailBarMaterial(BLENDER_TRACK.railBlack, {
          metalness: 0.82,
          roughness: 0.25,
          envMapIntensity: 0.5,
          opacity: 1,
        }),
      []
    );

    if (webBars.length === 0 && railBars.length === 0) return null;

    return (
      <group name="InstancedTrack">
        <InstancedTrackMeshLayer
          layerId="track-bars-web"
          bars={webBars}
          material={materialWeb}
          onSelect={onSelect}
        />
        <InstancedTrackMeshLayer
          layerId="track-bars-rail"
          bars={railBars}
          material={materialRail}
          onSelect={onSelect}
        />
      </group>
    );
  }
);

InstancedTrack.displayName = 'InstancedTrack';
export default InstancedTrack;
